const VOWELS = /[aeiou]/i;

/**
 * True if `token` looks like a random id chunk (nanoid / hash), not a normal word.
 * Nanoid(5) is drawn from [A-Za-z0-9_-]; we avoid stripping real 5-letter words when possible.
 */
function looksLikeShortUniquenessToken(token: string): boolean {
	if (token.length !== 5) {
		return false;
	}
	if (!/^[A-Za-z0-9_-]+$/.test(token)) {
		return false;
	}
	/** Strong signal: no vowels → very unlikely to be an English word used as a slug chunk. */
	const vowelCount = [...token].filter((ch) => VOWELS.test(ch)).length;
	if (vowelCount === 0) {
		return true;
	}
	/** Many vowels → likely a word (e.g. "about"); keep it. */
	if (vowelCount >= 3) {
		return false;
	}
	/** Digit or underscore/hyphen → typical of nanoid, not plain words like "world". */
	if (/[0-9_-]/.test(token)) {
		return true;
	}
	return false;
}

/**
 * Strips a trailing ` ` + 5-char uniqueness token when the segment decodes to words
 * with spaces (e.g. slug stored with spaces + nanoid chunk).
 */
function stripTrailingSpaceSeparatedUniquenessToken(s: string): string {
	const m = s.match(/^(.+)\s+([A-Za-z0-9_-]{5})$/);
	if (!m) {
		return s;
	}
	const token = m[2];
	if (!looksLikeShortUniquenessToken(token)) {
		return s;
	}
	return m[1].trimEnd();
}

/**
 * Strips machine uniqueness suffixes from a URL path segment used as a human title:
 * - `-` + 8 hex chars (topic slug helper `buildTopicSlug`)
 * - `-` + 5 chars when they match {@link looksLikeShortUniquenessToken} (`generateSlug`: `slugify`-`nanoid(5)`)
 * - ` ` + 5 chars when they match {@link looksLikeShortUniquenessToken} (decoded segments with spaces)
 *
 * Segments with no such suffix (settings, discover, plain words) are returned unchanged.
 */
export function stripGeneratedSlugSuffixFromSegment(segment: string): string {
	let decoded = segment;
	try {
		decoded = decodeURIComponent(segment);
	} catch {
		decoded = segment;
	}

	let out = decoded;

	const withoutHex8 = out.replace(/-[a-f0-9]{8}$/i, "");
	if (withoutHex8 !== out && withoutHex8.length > 0) {
		out = withoutHex8;
	}

	/** `generateSlug` appends `~` + nanoid(5) to avoid clashing with kebab slugs. */
	const tilde = out.indexOf("~");
	if (tilde > 0) {
		const rest = out.slice(tilde + 1);
		if (rest.length === 5 && looksLikeShortUniquenessToken(rest)) {
			out = out.slice(0, tilde);
		}
	}

	const lastHyphen = out.lastIndexOf("-");
	if (lastHyphen > 0) {
		const possibleToken = out.slice(lastHyphen + 1);
		if (
			possibleToken.length === 5 &&
			looksLikeShortUniquenessToken(possibleToken)
		) {
			out = out.slice(0, lastHyphen);
		}
	}

	out = stripTrailingSpaceSeparatedUniquenessToken(out);

	return out;
}

/**
 * Formats a raw URL segment for breadcrumb display: drops uniqueness suffixes, then
 * turns kebab-case into spaces with sentence-style capitalization on the first letter.
 */
export function formatPathSegmentBreadcrumbLabel(segment: string): string {
	const base = stripGeneratedSlugSuffixFromSegment(segment);
	if (base.length === 0) {
		return segment;
	}
	const withSpaces = base.replace(/-/g, " ");
	return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
}
